import clsx from "clsx"
import { useState } from "react"

import { faIcon } from "@utils/faIcon"
import { ifSpaceOrEnter } from "@utils/keyboard"

import { ExtraTouchArea, Skeleton, TruncatableText } from "@particles/index"

import { Icon } from "@atoms/index"

import styles from "./GroupLabel.module.scss"
import type { GroupLabelProps } from "./GroupLabel.types"

export default function GroupLabel(props: GroupLabelProps) {
    const {
        variant,
        prefixSlot = null,
        label,
        middleSlot = null,
        counter = "",
        description = "",
        suffixSlot = null,

        isCollapsible = false,
        onCollapse,

        showPrefixSlot = true,
        showMiddleSlot = true,

        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isDisabled = false,
        isLoading = false,
    } = props

    const [isLabelTruncated, setIsLabelTruncated] = useState(false)
    const [isCollapsed, setIsCollapsed] = useState(true)

    const isInteractive = isCollapsible && variant !== "secondary" && !isDisabled && !isLoading

    const allowPrefixSlot = showPrefixSlot
    const allowMiddleSlot = showMiddleSlot && variant !== "tertiary"
    const shouldDisplayPrefixSlot = allowPrefixSlot && Boolean(prefixSlot)
    const shouldDisplayMiddleSlot = allowMiddleSlot && Boolean(middleSlot)
    const shouldDisplayCounter = Boolean(counter) && variant !== "tertiary"
    const shouldDisplayDescription = !isLabelTruncated && Boolean(description) && variant !== "tertiary"
    const shouldDisplayBullet1 = variant !== "tertiary" && (shouldDisplayMiddleSlot || shouldDisplayCounter)
    const shouldDisplayBullet2 = shouldDisplayDescription

    function toggleCollapse() {
        if (!isInteractive) {
            return
        }

        setIsCollapsed((previous) => {
            const next = !previous
            onCollapse?.(next)
            return next
        })
    }

    function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
        if (isInteractive) {
            ifSpaceOrEnter(event, () => {
                toggleCollapse()
            })
        }
    }

    const rootClassName = clsx(styles.base, styles[`${variant}Variant`], {
        [styles.isInteractive]: isInteractive,
        [styles.isDisabled]: isDisabled,
        [styles.isLoading]: isLoading,
        [styles.isFocused]: _isFocused,
        [styles.isHovered]: _isHovered,
        [styles.isPressed]: isPressed,
        [styles.isCollapsed]: isCollapsed,
        [styles.isExpanded]: !isCollapsed,
    })

    const chevronClassName = clsx(styles.chevron, {
        [styles.chevronCollapsed]: isCollapsed,
        [styles.chevronExpanded]: !isCollapsed,
    })

    if (isLoading) {
        return (
            <div
                className={rootClassName}
                role={isInteractive ? "button" : undefined}
                tabIndex={isInteractive ? 0 : -1}
                onClick={isInteractive ? toggleCollapse : undefined}
                onKeyDown={handleKeyDown}
                aria-expanded={isInteractive ? !isCollapsed : undefined}
                data-collapsed={isCollapsed ? "true" : "false"}
            >
                <div className={styles.labelContainer}>
                    <Skeleton width={96} height={10} borderRadius={0} />
                </div>
            </div>
        )
    } else {
        return (
            <div
                className={rootClassName}
                role={isInteractive ? "button" : undefined}
                tabIndex={isInteractive ? 0 : -1}
                onClick={isInteractive ? toggleCollapse : undefined}
                onKeyDown={handleKeyDown}
                aria-expanded={isInteractive ? !isCollapsed : undefined}
                data-collapsed={isCollapsed ? "true" : "false"}
            >
                <div className={styles.labelContainer}>
                    {isCollapsible && variant === "primary" && (
                        <Icon
                            icon={faIcon({ name: "caret-right", style: "solid" })}
                            className={chevronClassName}
                            size={12}
                            container="square"
                        />
                    )}
                    <div className={styles.containerPaddings}>
                        {shouldDisplayPrefixSlot ? prefixSlot : null}
                        <TruncatableText
                            text={label}
                            className={styles.label}
                            tooltipOnTruncate={true}
                            onTruncationChange={setIsLabelTruncated}
                        />
                        {isCollapsible && variant === "tertiary" && (
                            <Icon
                                icon={faIcon({ name: "caret-right", style: "solid" })}
                                className={chevronClassName}
                                size={12}
                                container="square"
                            />
                        )}
                        {shouldDisplayBullet1 ? <span className={styles.bullet1}>・</span> : null}
                        {shouldDisplayMiddleSlot ? middleSlot : null}
                        {shouldDisplayCounter ? <span className={styles.counter}>{counter}</span> : null}
                        {shouldDisplayBullet2 ? <span className={styles.bullet2}>・</span> : null}
                        {shouldDisplayDescription ? (
                            <TruncatableText
                                text={description}
                                className={styles.description}
                                tooltipOnTruncate={true}
                            />
                        ) : null}
                    </div>
                    <div className={styles.cta}>
                        <ExtraTouchArea extraTouchArea={2}>{suffixSlot}</ExtraTouchArea>
                    </div>
                </div>
            </div>
        )
    }
}
